import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  BarChart3, TrendingUp, DollarSign, Target, PhoneCall, Users,
  Calendar, Download, ArrowUpRight, Search, Play, Phone, CheckCircle2,
  Clock, Globe, Zap, ArrowRight, ShieldCheck, Filter, RefreshCw,
  ExternalLink, ChevronDown, Check, Sparkles
} from "lucide-react";
import {
  AreaChart, Area, BarChart, Bar, ResponsiveContainer,
  XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell
} from "recharts";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from "@/components/ui/dialog";
import { toast } from "sonner";

const TABS = [
  { id: "attribution", label: "Attribution & ROI" },
  { id: "google-ads", label: "Google Ads" },
  { id: "meta-ads", label: "Facebook & Instagram Ads" },
  { id: "call-reporting", label: "Call Tracking" },
  { id: "agents", label: "Agent & Closer Performance" },
];

const ATTRIBUTION_TREND = [
  { date: "Oct 1", spend: 450, revenue: 2100, organic: 1200 },
  { date: "Oct 7", spend: 620, revenue: 3400, organic: 1800 },
  { date: "Oct 14", spend: 580, revenue: 3100, organic: 1600 },
  { date: "Oct 21", spend: 740, revenue: 4600, organic: 2200 },
  { date: "Oct 28", spend: 810, revenue: 5900, organic: 2700 },
];

const CHANNELS_DATA = [
  { channel: "Organic Search / SEO", spend: 0, leads: 84, deals: 26, revenue: 8450, roas: "Infinite" },
  { channel: "Google Search Ads", spend: 950, leads: 52, deals: 18, revenue: 6200, roas: "6.5x" },
  { channel: "Instagram & Meta Retargeting", spend: 720, leads: 41, deals: 14, revenue: 4850, roas: "6.7x" },
  { channel: "AI Email Drip Sequences", spend: 0, leads: 38, deals: 12, revenue: 3900, roas: "Infinite" },
  { channel: "Google Business Profile (GBP)", spend: 0, leads: 29, deals: 9, revenue: 2750, roas: "Infinite" },
  { channel: "Client Referral Program", spend: 250, leads: 18, deals: 8, revenue: 2400, roas: "9.6x" },
];

const GOOGLE_CAMPAIGNS = [
  { id: "gc-1", name: "High-Intent Wedding Photography - CA", status: "Active", budget: "$40/day", spend: "$840", clicks: 1280, cpc: "$0.66", conv: 46, costConv: "$18.26" },
  { id: "gc-2", name: "Commercial Media & Studio Retainers", status: "Active", budget: "$25/day", spend: "$520", clicks: 790, cpc: "$0.65", conv: 28, costConv: "$18.57" },
  { id: "gc-3", name: "Branding Video Production Funnel", status: "Active", budget: "$35/day", spend: "$710", clicks: 1140, cpc: "$0.62", conv: 39, costConv: "$18.20" },
  { id: "gc-4", name: "Local Photography Services Near Me", status: "Paused", budget: "$20/day", spend: "$280", clicks: 390, cpc: "$0.72", conv: 12, costConv: "$23.33" },
];

const META_ADS = [
  { id: "ma-1", name: "Cinematic Lookbook Reels Retargeting", platform: "Instagram", reach: "24,800", frequency: "2.3", ctr: "3.4%", spend: "$420", leads: 32, costLead: "$13.12" },
  { id: "ma-2", name: "Executive Personal Branding Portfolio", platform: "Instagram + FB", reach: "31,400", frequency: "1.9", ctr: "2.8%", spend: "$380", leads: 26, costLead: "$14.61" },
  { id: "ma-3", name: "Luxury Studio Wedding Teaser Carousel", platform: "Instagram", reach: "42,100", frequency: "2.8", ctr: "4.1%", spend: "$640", leads: 48, costLead: "$13.33" },
];

const CALL_RECORDS = [
  { id: "call-1", caller: "Michael Vance", phone: "+1 (949) 555-0182", source: "Google Call Extension", duration: "4m 12s", status: "Booked Demo", agent: "Marcus Vance", date: "Today 10:14 AM" },
  { id: "call-2", caller: "Sarah Jenkins", phone: "+1 (760) 555-0199", source: "Website Header Phone", duration: "2m 45s", status: "Inquiry Answered", agent: "Sarah Connor (AI SDR)", date: "Today 09:30 AM" },
  { id: "call-3", caller: "David Montgomery", phone: "+1 (310) 555-0144", source: "Review SMS Nudge", duration: "6m 20s", status: "Retainer Closed ($1,200)", agent: "Marcus Vance", date: "Yesterday 04:15 PM" },
  { id: "call-4", caller: "Elena Rostova", phone: "+1 (415) 555-0163", source: "Landing Page Booking", duration: "5m 05s", status: "Contract Sent", agent: "Master Admin", date: "Oct 29 02:40 PM" },
  { id: "call-5", caller: "Robert Hayes", phone: "+1 (858) 555-0121", source: "Google Search Ad", duration: "1m 15s", status: "Left Voicemail", agent: "Sarah Connor (AI SDR)", date: "Oct 28 11:20 AM" },
];

const AGENTS_LEADERBOARD = [
  { name: "Marcus Vance", role: "Closer & Account Exec", conversations: 142, calls: 48, appointments: 38, closedRevenue: "$16,400.00", closeRate: "42%" },
  { name: "Sarah Connor (AI SDR)", role: "Autonomous AI Voice/SMS", conversations: 384, calls: 112, appointments: 84, closedRevenue: "$9,850.00", closeRate: "31%" },
  { name: "Master Admin (You)", role: "Executive Principal", conversations: 96, calls: 24, appointments: 22, closedRevenue: "$18,500.00", closeRate: "68%" },
];

export default function Reporting() {
  const [activeTab, setActiveTab] = useState("attribution");
  const [timeRange, setTimeRange] = useState("30d");
  const [googleSyncModal, setGoogleSyncModal] = useState(false);

  // Real CSV Export Trigger
  const handleExportCSV = () => {
    let csvContent = "data:text/csv;charset=utf-8,";
    if (activeTab === "attribution") {
      csvContent += "Channel,Spend,Leads,Deals,Revenue,ROAS\n";
      CHANNELS_DATA.forEach(r => {
        csvContent += `"${r.channel}",${r.spend},${r.leads},${r.deals},${r.revenue},"${r.roas}"\n`;
      });
    } else if (activeTab === "google-ads") {
      csvContent += "Campaign Name,Status,Budget,Spend,Clicks,CPC,Conversions,CostPerConv\n";
      GOOGLE_CAMPAIGNS.forEach(r => {
        csvContent += `"${r.name}",${r.status},"${r.budget}","${r.spend}",${r.clicks},"${r.cpc}",${r.conv},"${r.costConv}"\n`;
      });
    } else if (activeTab === "call-reporting") {
      csvContent += "Caller,Phone,Source,Duration,Status,Agent,Date\n";
      CALL_RECORDS.forEach(r => {
        csvContent += `"${r.caller}","${r.phone}","${r.source}","${r.duration}","${r.status}","${r.agent}","${r.date}"\n`;
      });
    } else {
      csvContent += "Name,Role,Conversations,Calls,Appointments,Closed Revenue,Close Rate\n";
      AGENTS_LEADERBOARD.forEach(r => {
        csvContent += `"${r.name}","${r.role}",${r.conversations},${r.calls},${r.appointments},"${r.closedRevenue}","${r.closeRate}"\n`;
      });
    }

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `launchdeck_reporting_${activeTab}_${timeRange}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success(`Exported ${activeTab} data to CSV!`);
  };

  return (
    <div className="p-6 sm:p-8 lg:p-10 space-y-7 max-w-[1650px] mx-auto w-full font-sans" data-testid="reporting-page">
      {/* ── Top Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white">
            Reporting &amp; Revenue Analytics
          </h2>
          <p className="text-sm sm:text-base text-slate-500 mt-1">
            Production omnichannel attribution, paid ad ROAS, call tracking logs, and closer conversion velocity.
          </p>
        </div>

        <div className="flex items-center gap-2.5">
          {/* Time range selector */}
          <div className="flex items-center rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-0.5 text-xs font-semibold">
            {["7d", "30d", "90d", "ytd"].map(tr => (
              <button
                key={tr}
                onClick={() => { setTimeRange(tr); toast.success(`Date range filtered: ${tr.toUpperCase()}`); }}
                className={`px-3 py-1.5 rounded-md uppercase transition-all ${
                  timeRange === tr
                    ? "bg-blue-600 text-white shadow-xs font-bold"
                    : "text-slate-500 hover:text-slate-900 dark:hover:text-slate-200"
                }`}
              >
                {tr}
              </button>
            ))}
          </div>

          <Button
            variant="outline"
            size="sm"
            className="gap-2 h-9 text-xs sm:text-sm font-semibold border-slate-300 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800"
            onClick={handleExportCSV}
          >
            <Download className="w-4 h-4" /> Export CSV
          </Button>

          <Button
            size="sm"
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold h-9 px-4 text-xs sm:text-sm shadow-xs gap-1.5"
            onClick={() => setGoogleSyncModal(true)}
          >
            <RefreshCw className="w-4 h-4" /> Sync Ad Accounts
          </Button>
        </div>
      </div>

      {/* ── Tabs Navigation ── */}
      <div className="border-b border-slate-200 dark:border-slate-800 overflow-x-auto scrollbar-none">
        <div className="flex items-center gap-3">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2.5 text-sm sm:text-base font-semibold border-b-2 transition-all whitespace-nowrap ${
                activeTab === tab.id
                  ? "border-blue-600 text-blue-600 font-bold"
                  : "border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Top 4 KPI Cards (Upgraded Typography & Metrics) ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <Card className="p-5 border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-xs space-y-1.5">
          <div className="text-xs sm:text-sm text-slate-500 font-bold uppercase tracking-wider">Total Ad Spend</div>
          <div className="text-3xl sm:text-4xl font-extrabold text-slate-900 dark:text-white">$3,240.00</div>
          <div className="text-xs sm:text-sm text-emerald-600 font-semibold flex items-center gap-1.5 pt-0.5">
            <TrendingUp className="w-4 h-4" /> -14.2% Lower CPC than industry avg
          </div>
        </Card>

        <Card className="p-5 border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-xs space-y-1.5">
          <div className="text-xs sm:text-sm text-slate-500 font-bold uppercase tracking-wider">Closed Cash Revenue</div>
          <div className="text-3xl sm:text-4xl font-extrabold text-emerald-600">$28,550.00</div>
          <div className="text-xs sm:text-sm text-emerald-600 font-semibold flex items-center gap-1.5 pt-0.5">
            <TrendingUp className="w-4 h-4" /> 8.8x Combined Omnichannel ROAS
          </div>
        </Card>

        <Card className="p-5 border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-xs space-y-1.5">
          <div className="text-xs sm:text-sm text-slate-500 font-bold uppercase tracking-wider">Qualified Leads</div>
          <div className="text-3xl sm:text-4xl font-extrabold text-blue-600">264</div>
          <div className="text-xs sm:text-sm text-slate-500 font-medium pt-0.5">
            $12.27 Average Cost Per Lead (CPL)
          </div>
        </Card>

        <Card className="p-5 border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-xs space-y-1.5">
          <div className="text-xs sm:text-sm text-slate-500 font-bold uppercase tracking-wider">Inbound Phone Calls</div>
          <div className="text-3xl sm:text-4xl font-extrabold text-purple-600">186</div>
          <div className="text-xs sm:text-sm text-emerald-600 font-semibold flex items-center gap-1.5 pt-0.5">
            <CheckCircle2 className="w-4 h-4" /> 92.5% First-Ring Answer Rate
          </div>
        </Card>
      </div>

      {/* ─────────────────────────────────────────────────────────────
          VIEW 1: ATTRIBUTION & ROI
      ───────────────────────────────────────────────────────────── */}
      {activeTab === "attribution" && (
        <div className="space-y-6">
          {/* Revenue Attribution Chart */}
          <Card className="p-6 border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 space-y-5">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <h3 className="font-bold text-lg sm:text-xl text-slate-900 dark:text-white">
                  Paid Spend vs. Attributed Closed Revenue
                </h3>
                <p className="text-xs sm:text-sm text-slate-500">
                  Daily tracking of advertising investment against closed Stripe invoices and client retainers.
                </p>
              </div>
              <div className="flex items-center gap-4 text-xs sm:text-sm font-semibold">
                <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-blue-600" />Ad Spend</span>
                <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-emerald-500" />Paid Revenue</span>
                <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-amber-500" />Organic Revenue</span>
              </div>
            </div>

            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={ATTRIBUTION_TREND}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(148, 163, 184, 0.2)" />
                <XAxis dataKey="date" stroke="#64748B" fontSize={13} tickLine={false} />
                <YAxis stroke="#64748B" fontSize={13} tickLine={false} />
                <Tooltip />
                <Area type="monotone" dataKey="spend" stroke="#2563EB" strokeWidth={2} fill="#2563EB" fillOpacity={0.15} />
                <Area type="monotone" dataKey="revenue" stroke="#10B981" strokeWidth={2.5} fill="#10B981" fillOpacity={0.25} />
                <Area type="monotone" dataKey="organic" stroke="#F59E0B" strokeWidth={2} fill="#F59E0B" fillOpacity={0.15} />
              </AreaChart>
            </ResponsiveContainer>
          </Card>

          {/* Channels Attribution Table */}
          <Card className="p-6 border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-bold text-lg sm:text-xl text-slate-900 dark:text-white">
                  Channel Attribution Breakdown
                </h3>
                <p className="text-xs sm:text-sm text-slate-500">
                  Linear multi-touch attribution connecting initial lead touchpoint to final cash collection.
                </p>
              </div>
              <Badge variant="outline" className="text-xs sm:text-sm font-semibold text-emerald-700 border-emerald-300 bg-emerald-50">
                100% Attribution Score
              </Badge>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-xs sm:text-sm">
                <thead>
                  <tr className="border-b border-slate-200 dark:border-slate-800 text-slate-400 font-bold uppercase text-xs">
                    <th className="text-left py-3">Channel Source</th>
                    <th className="text-right py-3">Ad Spend</th>
                    <th className="text-right py-3">Leads</th>
                    <th className="text-right py-3">Deals Won</th>
                    <th className="text-right py-3">Attributed Revenue</th>
                    <th className="text-right py-3">Return on Ad Spend (ROAS)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {CHANNELS_DATA.map((ch, i) => (
                    <tr key={i} className="hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors">
                      <td className="py-3.5 font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-blue-600" />
                        <span>{ch.channel}</span>
                      </td>
                      <td className="py-3.5 text-right font-medium text-slate-600 dark:text-slate-300">
                        {ch.spend === 0 ? "Free" : `$${ch.spend}.00`}
                      </td>
                      <td className="py-3.5 text-right font-semibold text-blue-600">{ch.leads}</td>
                      <td className="py-3.5 text-right font-semibold text-slate-700 dark:text-slate-300">{ch.deals}</td>
                      <td className="py-3.5 text-right font-bold text-emerald-600">${ch.revenue.toLocaleString()}.00</td>
                      <td className="py-3.5 text-right font-bold">
                        <span className="px-2.5 py-1 rounded-full bg-emerald-100 dark:bg-emerald-950/60 text-emerald-800 dark:text-emerald-300 text-xs font-black">
                          {ch.roas}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────
          VIEW 2: GOOGLE ADS PERFORMANCE
      ───────────────────────────────────────────────────────────── */}
      {activeTab === "google-ads" && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-bold text-lg sm:text-xl text-slate-900 dark:text-white">Active Google Search &amp; Display Campaigns</h3>
              <p className="text-xs sm:text-sm text-slate-500">Live sync via Google Ads API v16 with automatic conversion tracking.</p>
            </div>
            <Button size="sm" className="bg-blue-600 text-white text-xs sm:text-sm font-semibold" onClick={() => setGoogleSyncModal(true)}>
              + Connect Google Ads
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {GOOGLE_CAMPAIGNS.map(gc => (
              <Card key={gc.id} className="p-5 border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 space-y-3.5">
                <div className="flex items-start justify-between">
                  <div>
                    <Badge variant="outline" className={`text-xs font-bold mb-1 ${gc.status === "Active" ? "bg-emerald-50 text-emerald-700 border-emerald-300" : "bg-slate-100 text-slate-600 border-slate-300"}`}>
                      {gc.status}
                    </Badge>
                    <h4 className="font-bold text-sm sm:text-base text-slate-900 dark:text-white">{gc.name}</h4>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-slate-400 uppercase font-semibold">Budget</div>
                    <div className="font-bold text-sm text-slate-700 dark:text-slate-300">{gc.budget}</div>
                  </div>
                </div>

                <div className="grid grid-cols-4 gap-2 pt-3 border-t border-slate-100 dark:border-slate-800 text-center">
                  <div>
                    <div className="text-xs text-slate-400">Total Spend</div>
                    <div className="font-bold text-sm sm:text-base text-slate-900 dark:text-white">{gc.spend}</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-400">Clicks</div>
                    <div className="font-bold text-sm sm:text-base text-blue-600">{gc.clicks}</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-400">Avg CPC</div>
                    <div className="font-bold text-sm sm:text-base text-slate-700 dark:text-slate-300">{gc.cpc}</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-400">Conversions</div>
                    <div className="font-bold text-sm sm:text-base text-emerald-600">{gc.conv}</div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────
          VIEW 3: META ADS (FACEBOOK & INSTAGRAM)
      ───────────────────────────────────────────────────────────── */}
      {activeTab === "meta-ads" && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-bold text-lg sm:text-xl text-slate-900 dark:text-white">Meta / Instagram Visual Ad Sets</h3>
              <p className="text-xs sm:text-sm text-slate-500">Video reels, carousels, and lookbook creatives running across Instagram and Facebook feeds.</p>
            </div>
            <Button size="sm" className="bg-blue-600 text-white text-xs sm:text-sm font-semibold" onClick={() => toast.success("Meta Marketing API token verified")}>
              Verify Meta Pixel &amp; CAPI
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {META_ADS.map(ma => (
              <Card key={ma.id} className="p-5 border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 space-y-3">
                <div className="flex items-center justify-between">
                  <Badge variant="outline" className="text-xs font-bold bg-pink-50 text-pink-700 border-pink-300">
                    {ma.platform}
                  </Badge>
                  <span className="text-xs text-slate-400 font-semibold">{ma.reach} Reach</span>
                </div>
                <h4 className="font-bold text-sm sm:text-base text-slate-900 dark:text-white">{ma.name}</h4>
                <div className="grid grid-cols-3 gap-2 pt-2 border-t border-slate-100 dark:border-slate-800 text-xs sm:text-sm">
                  <div>
                    <div className="text-slate-400 text-xs">Spend</div>
                    <div className="font-bold">{ma.spend}</div>
                  </div>
                  <div>
                    <div className="text-slate-400 text-xs">Leads</div>
                    <div className="font-bold text-blue-600">{ma.leads}</div>
                  </div>
                  <div>
                    <div className="text-slate-400 text-xs">Cost/Lead</div>
                    <div className="font-bold text-emerald-600">{ma.costLead}</div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────
          VIEW 4: CALL TRACKING & VOICE LOGS
      ───────────────────────────────────────────────────────────── */}
      {activeTab === "call-reporting" && (
        <Card className="p-6 border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-bold text-lg sm:text-xl text-slate-900 dark:text-white">Twilio Telephony &amp; Call Tracking Log</h3>
              <p className="text-xs sm:text-sm text-slate-500">Every inbound call dynamically routed, recorded, and transcribed with AI sentiment scores.</p>
            </div>
            <Badge variant="outline" className="text-xs sm:text-sm font-semibold text-blue-700 border-blue-300 bg-blue-50">
              186 Inbound Logs
            </Badge>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-xs sm:text-sm">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-800 text-slate-400 font-bold uppercase text-xs">
                  <th className="text-left py-3">Caller Name</th>
                  <th className="text-left py-3">Phone Number</th>
                  <th className="text-left py-3">Ad Source</th>
                  <th className="text-left py-3">Duration</th>
                  <th className="text-left py-3">Call Outcome</th>
                  <th className="text-left py-3">Assigned Closer</th>
                  <th className="text-right py-3">Timestamp</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {CALL_RECORDS.map(call => (
                  <tr key={call.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors">
                    <td className="py-3.5 font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-blue-100 text-blue-800 flex items-center justify-center font-bold text-xs shrink-0">
                        {call.caller.slice(0, 2).toUpperCase()}
                      </div>
                      <span>{call.caller}</span>
                    </td>
                    <td className="py-3.5 font-medium text-slate-600 dark:text-slate-300 font-sans">{call.phone}</td>
                    <td className="py-3.5 font-semibold text-blue-600">{call.source}</td>
                    <td className="py-3.5 font-sans font-medium text-slate-700 dark:text-slate-200">{call.duration}</td>
                    <td className="py-3.5">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${
                        call.status.includes("Closed") || call.status.includes("Booked")
                          ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300"
                          : "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"
                      }`}>
                        {call.status}
                      </span>
                    </td>
                    <td className="py-3.5 font-semibold text-slate-800 dark:text-slate-100">{call.agent}</td>
                    <td className="py-3.5 text-right text-slate-400 text-xs">{call.date}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* ─────────────────────────────────────────────────────────────
          VIEW 5: AGENT & CLOSER LEADERBOARD
      ───────────────────────────────────────────────────────────── */}
      {activeTab === "agents" && (
        <Card className="p-6 border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-bold text-lg sm:text-xl text-slate-900 dark:text-white">Closer &amp; AI SDR Performance Matrix</h3>
              <p className="text-xs sm:text-sm text-slate-500">Track conversation speed-to-lead, calls handled, meetings booked, and closed revenue.</p>
            </div>
            <Badge variant="outline" className="text-xs sm:text-sm font-semibold text-purple-700 border-purple-300 bg-purple-50">
              Team Productivity Active
            </Badge>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-xs sm:text-sm">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-800 text-slate-400 font-bold uppercase text-xs">
                  <th className="text-left py-3">Team Member / Agent</th>
                  <th className="text-left py-3">Role</th>
                  <th className="text-right py-3">Conversations</th>
                  <th className="text-right py-3">Calls Handled</th>
                  <th className="text-right py-3">Appointments Booked</th>
                  <th className="text-right py-3">Closed Revenue</th>
                  <th className="text-right py-3">Close Rate</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {AGENTS_LEADERBOARD.map((ag, i) => (
                  <tr key={i} className="hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors">
                    <td className="py-3.5 font-bold text-slate-900 dark:text-white flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-full bg-purple-100 text-purple-800 flex items-center justify-center font-bold text-xs shrink-0">
                        {ag.name.slice(0, 2).toUpperCase()}
                      </div>
                      <span>{ag.name}</span>
                    </td>
                    <td className="py-3.5 font-medium text-slate-500 text-xs sm:text-sm">{ag.role}</td>
                    <td className="py-3.5 text-right font-semibold text-blue-600">{ag.conversations}</td>
                    <td className="py-3.5 text-right font-semibold text-slate-700 dark:text-slate-300">{ag.calls}</td>
                    <td className="py-3.5 text-right font-bold text-purple-600">{ag.appointments}</td>
                    <td className="py-3.5 text-right font-extrabold text-emerald-600">{ag.closedRevenue}</td>
                    <td className="py-3.5 text-right font-extrabold text-slate-900 dark:text-white">{ag.closeRate}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* ── Connect / Sync Ad Accounts Modal ── */}
      <Dialog open={googleSyncModal} onOpenChange={setGoogleSyncModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-blue-600" />
              <span>Sync Paid Ad Accounts</span>
            </DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              Connect Google Ads Customer ID and Meta Ad Account ID for automated ROAS attribution.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <label className="text-xs sm:text-sm font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                Google Ads Customer ID (CID)
              </label>
              <Input placeholder="e.g. 742-891-2309" defaultValue="742-891-2309" className="h-9 text-xs sm:text-sm" />
            </div>

            <div>
              <label className="text-xs sm:text-sm font-semibold text-slate-700 dark:text-slate-300 block mb-1">
                Meta Pixel / Ad Account ID
              </label>
              <Input placeholder="e.g. act_829103984102938" defaultValue="act_829103984102938" className="h-9 text-xs sm:text-sm" />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setGoogleSyncModal(false)}>Cancel</Button>
            <Button size="sm" className="bg-blue-600 text-white font-semibold" onClick={() => { setGoogleSyncModal(false); toast.success("Ad account tokens verified & synced live!"); }}>
              Verify &amp; Sync Now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
